import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { merge } from 'lodash-es';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { generateAiDraft } from '#src/app/pages/startAPetitionV2/shared/api';
import type { SandboxContract } from '#src/app/pages/startAPetitionV2/shared/components';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

type SubmitAction = SandboxContract['actions']['submit'];
type SubmitParams = Parameters<SubmitAction>[0];

type UseChatAgentSubmitReturn = ModelHookResult<
	{ loading: boolean },
	{ onSubmit: (params: SubmitParams, submit: SubmitAction) => void }
>;

/**
 * Gates the chat step's `submit` message on AI draft (+ image) generation: the chat
 * conversation itself only collects `issuePrompt`/`personalStory`, so the actual draft
 * has to be generated here, host-side, before the step can hand off to the real sandbox
 * `submit`. Includes the target-'start' (opt-out) case forwarded as-is, no mutation needed.
 */
export function useChatAgentSubmit(): UseChatAgentSubmitReturn {
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const { mutateAsync: createImageJobAsync } = useImageJob();

	const { mutate, isPending, isSuccess } = useMutation({
		mutationFn: async (input: { mainPrompt: string; personalStory?: string }) => {
			const [aiDraftResponse, imageResponse] = await Promise.allSettled([
				generateAiDraft(input, utilityContext, track),
				createImageJobAsync(input),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			return { ...aiDraftResponse.value, ...image };
		},
	});

	const onSubmit = useCallback(
		(params: SubmitParams, submit: SubmitAction) => {
			if (params.target === 'start') {
				// opting out invalidates a previous chat completion's `flow` marker — without this,
				// a second pass through the manual issue/personal-story flow would land on `preview`
				// still flagged as `flow: 'chat'`, misrouting `preview`'s `back` transition.
				submit({ ...params, draft: merge({}, params.draft, { input: { flow: null } }) });
				return;
			}

			const issuePrompt = params.draft?.input?.issuePrompt ?? '';
			const personalStory = params.draft?.input?.personalStory ?? '';

			mutate(
				{ mainPrompt: issuePrompt, personalStory },
				{
					onSuccess: (response) => {
						const recommendedDraft = { ask: response.ask, description: response.description, image: null };
						const metadata = { imageJobId: response.image?.responseId ?? null };

						submit({
							draft: {
								recommendedDraft,
								petitionDraft: recommendedDraft,
								editingDraft: null,
								metadata,
								input: { flow: 'chat' },
							},
							target: 'ai',
						});
					},
					onError: (_error: unknown) => {
						// TODO: handle error
					},
				},
			);
		},
		[mutate],
	);

	// isSuccess stays true after the mutation resolves, covering the gap between submit()
	// firing and the state machine actually navigating away from this step — otherwise the
	// iframe would flash back into view for a render or two.
	const isSubmitting = isPending || isSuccess;

	return { data: { loading: isSubmitting }, actions: { onSubmit } };
}
